import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "contactStatus", "memo", "lockInfo", "confirmedSchedule", "confirmedScheduleEnd"]
  static values = { selectionListId: Number, projectId: Number, interviewDurationMinutes: Number }

  connect() {
    this.draftTimers = {}
    this.lockTimers = {}
  }

  disconnect() {
    Object.values(this.draftTimers).forEach(t => clearTimeout(t))
    Object.values(this.lockTimers).forEach(t => clearTimeout(t))
  }

  // ステータス変更 → 保存のみ（アンロックは blur に任せる）
  saveDraft(event) {
    const row = event.target.closest("[data-candidate-id]")
    if (!row) return
    this.saveDraftForRow(row)
  }

  // メモ入力 → debounce して保存
  debouncedSaveDraft(event) {
    const row = event.target.closest("[data-candidate-id]")
    if (!row) return
    const candidateId = row.dataset.candidateId

    if (this.draftTimers[candidateId]) clearTimeout(this.draftTimers[candidateId])
    this.draftTimers[candidateId] = setTimeout(() => {
      this.saveDraftForRow(row)
    }, 500)
  }

  // focus / mousedown → ロック取得（WebSocket のみ、HTTP 不要）
  lockCandidate(event) {
    const row = event.target.closest("[data-candidate-id]")
    if (!row) return
    const candidateId = row.dataset.candidateId

    // NOTE: 同一行内の移動で pending な unlock があればキャンセル
    if (this.lockTimers[candidateId]) {
      clearTimeout(this.lockTimers[candidateId])
      this.lockTimers[candidateId] = null
    }

    const channelController = this.findChannelController()
    if (channelController) {
      channelController.lockCandidate(candidateId)
    }
  }

  // blur → ロック解放（WebSocket のみ）
  unlockCandidate(event) {
    const row = event.target.closest("[data-candidate-id]")
    if (!row) return
    const candidateId = row.dataset.candidateId

    this.lockTimers[candidateId] = setTimeout(() => {
      this.lockTimers[candidateId] = null
      const channelController = this.findChannelController()
      if (channelController) {
        channelController.unlockCandidate(candidateId)
      }
    }, 300)
  }

  async saveDraftForRow(row) {
    const candidateId = row.dataset.candidateId
    const statusSelect = row.querySelector("[data-workspace-candidates-target='contactStatus']")
    const memoInput = row.querySelector("[data-workspace-candidates-target='memo']")

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = `/admin/projects/${this.projectIdValue}/selection_lists/${this.selectionListIdValue}/entries/save_draft`

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          candidate_id: candidateId,
          contact_status: statusSelect?.value || "",
          memo: memoInput?.value || "",
          confirmed_schedule: row.querySelector("[data-workspace-candidates-target='confirmedSchedule']")?.value || "",
          confirmed_schedule_end: row.querySelector("[data-workspace-candidates-target='confirmedScheduleEnd']")?.value || ""
        })
      })
      // NOTE: broadcast の到達遅延に依存せず、自分の操作はレスポンスで即座に DOM 反映する。
      await this.applyResponsePayload(response)
    } catch (error) {
      console.error("Failed to save draft:", error)
    }
  }

  async applyResponsePayload(response) {
    if (!response.ok) return
    const contentType = response.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) return
    const data = await response.json()
    if (!data || !data.type || data.type === "noop") return
    const channelController = this.findChannelController()
    if (channelController) channelController.handleMessage(data)
  }

  // 開始時間変更 → インタビュー時間が設定されていれば終了時刻を自動計算
  autoFillEndTime(event) {
    const row = event.target.closest("[data-candidate-id]")
    if (!row) return
    const startValue = event.target.value
    if (!startValue || !this.interviewDurationMinutesValue) return
    const endValue = this.addMinutesToDatetimeLocal(startValue, this.interviewDurationMinutesValue)

    // confirmed-schedule コントローラーがあれば setEndTime を呼ぶ
    const csEl = event.target.closest("[data-controller~='confirmed-schedule']")
    if (csEl) {
      const csCtrl = this.application.getControllerForElementAndIdentifier(csEl, "confirmed-schedule")
      if (csCtrl && typeof csCtrl.setEndTime === "function") {
        csCtrl.setEndTime(endValue)
        return
      }
    }

    // フォールバック: datetime-picker（一括パネル等）
    const endHidden = row.querySelector("[data-workspace-candidates-target='confirmedScheduleEnd']")
    if (!endHidden) return
    this.setDatetimePickerValue(endHidden, endValue)
  }

  setDatetimePickerValue(hiddenInput, value) {
    const wrapper = hiddenInput.closest("[data-controller~='datetime-picker']")
    if (!wrapper) return
    const dpCtrl = this.application.getControllerForElementAndIdentifier(wrapper, "datetime-picker")
    if (dpCtrl && typeof dpCtrl.setValue === "function") dpCtrl.setValue(value)
  }

  addMinutesToDatetimeLocal(datetimeLocal, minutes) {
    const [datePart, timePart] = datetimeLocal.split("T")
    const [year, month, day] = datePart.split("-").map(Number)
    const [hour, minute] = timePart.split(":").map(Number)
    const d = new Date(year, month - 1, day, hour, minute + minutes)
    const pad = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  findChannelController() {
    const channelEl = this.element.closest("[data-controller~='workspace-channel']")
    if (!channelEl) return null
    return this.application.getControllerForElementAndIdentifier(channelEl, "workspace-channel")
  }
}
